/**
 * versona_lab — the GROUP simulation engine behind the Versona Lab.
 *
 * One engine, three drivers: the live VersonaLabModule window, the
 * `?probe=versonalab` suite, and the headless `verify:versonalab` harness all
 * run THIS code — so what the panel shows, the probe measures, and CI asserts
 * are the same observations. The engine is deliberately conn-structural
 * (duck-typed over the generated SpacetimeDB SDK, browser or Node — the
 * bindings are identical) and DOM-free.
 *
 * What it does: picks a cast of adult versonas in one settlement, stages
 * scripted GROUP scenarios through the existing admin debug reducers (hunger
 * crisis, feud, friendship, cheer, direct command), samples every cast
 * member's full state on an interval, diffs the event tables into a
 * who-did-what-with-whom interaction log, evaluates behavior invariants, and
 * renders the whole run as a markdown + JSON report. Every injection is
 * recorded and RESTORED afterwards (stats, grudges, pair familiarity; treated
 * agents get a clear-affect — organic episodes on those few are collateral,
 * stated in the report).
 *
 * Live-world laws honored (the sim never pauses): hard checks are mechanical
 * outcomes with generous polling; probabilistic behaviors (encounter spawns,
 * copes, brawls) are soft observations, never gate failures. Goals are
 * re-asserted through the window because npcSimTick's brain legitimately
 * overwrites them.
 */

import { nameFromTables } from './versona_names.js';

// ─── Row shapes (structural subset of the generated bindings) ────────────────

interface Ts { microsSinceUnixEpoch: bigint }

export interface AgentRow {
  versonaId: bigint; worldId: bigint; settlementId: bigint; job: string;
  boldness: number; greed: number; diligence: number; sociability: number;
  homeX: number; homeZ: number; wallet: bigint; carry: number; carryGood: string;
  hunger: number; needRest: number; needSocial: number; mood: number;
}
interface VersonaRow { versonaId: bigint; archetype: string; state: string }
interface StateRow { versonaId: bigint; curX: number; curZ: number; state: string }
interface GoalRow { versonaId: bigint; x: number; z: number; state: string }
interface IntentRow { versonaId: bigint; activity: string; commitment: number }
interface MindRow { versonaId: bigint; joy: number; anger: number; fear: number; hungerStreak: number }
interface MoodRow { versonaId: bigint; pleasureX10: number; arousalX10: number; dominanceX10: number }
interface EmotionRow { emotionId: bigint; versonaId: bigint; etype: string; targetId: bigint; causeText: string; intensity: number; halfLifeS: number; attendantOf: bigint; elicitedAt: Ts }
interface GrudgeRow { grudgeId: bigint; versonaId: bigint; targetId: bigint; causeText: string; intensity: number; offenses: number }
interface PairRow { pairId: bigint; aId: bigint; bId: bigint; familiarity: number }
interface FavorRow { favorId: bigint; aId: bigint; bId: bigint; balance: number; note: string }
interface EncounterRow { encounterId: bigint; settlementId: bigint; aId: bigint; bId: bigint; playerHex: string; kind: string; state: string; tier: string }
interface EventRow { eventId: bigint; settlementId: bigint; versonaId: bigint; kind: string; detail: string; at: Ts }
interface ChronicleRow { chronId: bigint; settlementId: bigint; kind: string; actorId: bigint; otherId: bigint; detail: string; at: Ts }
interface ChatRow { chatId: bigint; versonaId: bigint; speaker: string; speakerName: string; text: string; at: Ts }
interface PantryRow { versonaId: bigint; foodDays: number }
interface LineageRow { childId: bigint; parentA: bigint; parentB: bigint; generation: number; bornAt: Ts }
interface MemoryRow { memoryId: bigint; versonaId: bigint; kind: string; text: string; importance: number }
interface FaithRow { versonaId: bigint; faithId: string; intensity: number }
interface EconRow { settlementId: bigint; centerX: number; centerZ: number; priceFood: number; treasury: bigint; mayorId: bigint }

interface LabReducers {
  debugSetAgentStat(a: { versonaId: bigint; field: string; value: bigint }): Promise<void>;
  debugElicitEmotion(a: { versonaId: bigint; etype: string; intensity: number; targetId: bigint; causeText: string }): Promise<void>;
  debugSetGrudge(a: { versonaId: bigint; targetId: bigint; intensity: number; causeText: string }): Promise<void>;
  debugSetPair(a: { aId: bigint; bId: bigint; familiarity: number }): Promise<void>;
  debugClearAffect(a: { versonaId: bigint }): Promise<void>;
  setVersonaGoal(a: { versonaId: bigint; x: number; z: number; state: string; ttlSeconds: number }): Promise<void>;
}

// ─── Conn access (duck-typed over browser AND Node SDK connections) ──────────

interface TableLike { iter(): Iterable<unknown> }

export function tbl<T>(conn: unknown, name: string): T[] {
  try {
    const t = (conn as { db: Record<string, TableLike | undefined> }).db[name];
    return t ? ([...t.iter()] as T[]) : [];
  } catch { return []; }
}

export function labReducers(conn: unknown): LabReducers | null {
  return ((conn as { reducers?: unknown } | null)?.reducers as LabReducers | undefined) ?? null;
}

/** Base subscriptions the lab needs (the two big append-only tables are scoped
 *  separately — see labScopedSubs). Chat is small (retention) and unscopable. */
export function labBaseSubs(): string[] {
  return [
    'SELECT * FROM versona', 'SELECT * FROM npc_agent', 'SELECT * FROM versona_state',
    'SELECT * FROM versona_goal', 'SELECT * FROM versona_intent',
    'SELECT * FROM npc_mind', 'SELECT * FROM npc_mood', 'SELECT * FROM versona_emotion',
    'SELECT * FROM versona_grudge', 'SELECT * FROM versona_pair', 'SELECT * FROM versona_favor',
    'SELECT * FROM versona_encounter', 'SELECT * FROM versona_chat',
    'SELECT * FROM settlement_econ', 'SELECT * FROM npc_pantry', 'SELECT * FROM versona_lineage',
    'SELECT * FROM faith_adherence',
    // V1 names: civ flavor + household surnames (birth civ first — versona_origin).
    'SELECT * FROM settlement_civ', 'SELECT * FROM versona_origin', 'SELECT * FROM household', 'SELECT * FROM household_member',
  ];
}

/** versona_memory is the biggest table in the database (106k rows — a per-versona
 *  cap × 6,671 versonas) and the lab reads it only for the SELECTED versona's top
 *  three memories: subscribe it per selection, never whole (2026-09-12). */
export function labVersonaSubs(versonaId: bigint): string[] {
  return [`SELECT * FROM versona_memory WHERE versona_id = ${versonaId}`, `SELECT * FROM memory_provenance WHERE versona_id = ${versonaId}`]; // + where each memory came from (2026-09-15)
}

/** npc_event ≈30/settlement and chronicle ≈200/settlement × 312 settlements —
 *  always subscribe these WHERE-scoped to the chosen settlement. */
export function labScopedSubs(settlementId: bigint): string[] {
  return [
    `SELECT * FROM npc_event WHERE settlement_id = ${settlementId}`,
    `SELECT * FROM chronicle WHERE settlement_id = ${settlementId}`,
  ];
}

// ─── Small pure helpers ──────────────────────────────────────────────────────

export function decayedIntensity(intensity: number, halfLifeS: number, elicitedAtMicros: bigint, nowMs: number): number {
  const ageS = Math.max(0, nowMs / 1000 - Number(elicitedAtMicros / 1_000_000n));
  return intensity * Math.pow(0.5, ageS / Math.max(1, halfLifeS));
}

export function labelOf(conn: unknown, id: bigint): string {
  const a = tbl<AgentRow>(conn, 'npcAgent').find((x) => x.versonaId === id);
  const v = tbl<VersonaRow>(conn, 'versona').find((x) => x.versonaId === id);
  // V1 — people go by NAME: "Bjorild (smith #9483)".
  return `${nameFromTables(conn, id).split(' ')[0]} (${a?.job ?? v?.archetype ?? '?'} #${id})`;
}

/** The fully-attributed form for dossiers/reports: "Bjorild Galridge, smith (#9483)". */
export function fullNameOf(conn: unknown, id: bigint): string {
  const a = tbl<AgentRow>(conn, 'npcAgent').find((x) => x.versonaId === id);
  return `${nameFromTables(conn, id)}, ${a?.job ?? 'settler'} (#${id})`;
}

const pairKey = (a: bigint, b: bigint): string => (a < b ? `${a}~${b}` : `${b}~${a}`);

// ─── Sampling ────────────────────────────────────────────────────────────────

export interface VersonaSnapshot {
  atMs: number; id: bigint; x: number; z: number;
  activity: string; goal: string;
  hunger: number; needRest: number; needSocial: number; mood: number; wallet: number;
  hungerStreak: number; pantryDays: number | null;
  pad: { p: number; a: number; d: number } | null;
  emotions: { etype: string; eff: number; target: bigint; cause: string }[];
  grudges: number; encounter: string | null;
}

export function sampleVersona(conn: unknown, id: bigint, nowMs: number): VersonaSnapshot | null {
  const a = tbl<AgentRow>(conn, 'npcAgent').find((x) => x.versonaId === id);
  if (!a) return null;
  const st = tbl<StateRow>(conn, 'versonaState').find((x) => x.versonaId === id);
  const goal = tbl<GoalRow>(conn, 'versonaGoal').find((x) => x.versonaId === id);
  const intent = tbl<IntentRow>(conn, 'versonaIntent').find((x) => x.versonaId === id);
  const mind = tbl<MindRow>(conn, 'npcMind').find((x) => x.versonaId === id);
  const mood = tbl<MoodRow>(conn, 'npcMood').find((x) => x.versonaId === id);
  const pantry = tbl<PantryRow>(conn, 'npcPantry').find((x) => x.versonaId === id);
  const enc = tbl<EncounterRow>(conn, 'versonaEncounter').find((x) => x.aId === id || x.bId === id);
  const emotions = tbl<EmotionRow>(conn, 'versonaEmotion')
    .filter((e) => e.versonaId === id)
    .map((e) => ({ etype: e.etype, eff: Math.round(decayedIntensity(e.intensity, e.halfLifeS, e.elicitedAt.microsSinceUnixEpoch, nowMs)), target: e.targetId, cause: e.causeText }))
    .filter((e) => e.eff >= 8)
    .sort((x, y) => y.eff - x.eff);
  return {
    atMs: nowMs, id, x: st?.curX ?? a.homeX, z: st?.curZ ?? a.homeZ,
    activity: intent?.activity ?? st?.state ?? '?',
    goal: goal ? `(${goal.x.toFixed(0)},${goal.z.toFixed(0)}) ${goal.state}` : '',
    hunger: a.hunger, needRest: a.needRest, needSocial: a.needSocial, mood: a.mood,
    wallet: Number(a.wallet), hungerStreak: mind?.hungerStreak ?? 0,
    pantryDays: pantry ? +pantry.foodDays.toFixed(2) : null,
    pad: mood ? { p: mood.pleasureX10 / 10, a: mood.arousalX10 / 10, d: mood.dominanceX10 / 10 } : null,
    emotions,
    grudges: tbl<GrudgeRow>(conn, 'versonaGrudge').filter((g) => g.versonaId === id).length,
    encounter: enc ? `${enc.kind}:${enc.state}${enc.tier ? `/${enc.tier}` : ''} with #${enc.aId === id ? enc.bId : enc.aId}` : null,
  };
}

/** New emotion episodes on `id` elicited after `sinceMicros` (injection-aware
 *  behavior detection — the sim timestamps every episode server-side). */
export function emotionsSince(conn: unknown, id: bigint, sinceMicros: bigint): EmotionRow[] {
  return tbl<EmotionRow>(conn, 'versonaEmotion')
    .filter((e) => e.versonaId === id && e.elicitedAt.microsSinceUnixEpoch > sinceMicros);
}

// ─── Interaction watch (diffs the append-only tables + encounter lifecycle) ──

export interface LabInteraction {
  atMs: number; src: 'event' | 'chronicle' | 'chat' | 'encounter';
  kind: string; actors: bigint[]; line: string;
}

export class InteractionWatch {
  private lastEvent = 0n;
  private lastChron = 0n;
  private lastChat = 0n;
  private encounters = new Map<bigint, string>(); // encounterId → last "state/tier"
  private favors = new Map<bigint, number>();     // favorId → last balance
  private primed = false;

  constructor(private readonly settlementId: bigint) {}

  /** First call baselines (nothing emitted for pre-existing rows). */
  drain(conn: unknown, nowMs: number): LabInteraction[] {
    const out: LabInteraction[] = [];
    const agentSet = new Set(tbl<AgentRow>(conn, 'npcAgent').filter((a) => a.settlementId === this.settlementId).map((a) => a.versonaId));

    // Row iteration is unordered — snapshot the threshold, collect, THEN
    // advance the high-water mark (advancing mid-scan can skip fresh rows).
    const evSince = this.lastEvent;
    const freshEv = tbl<EventRow>(conn, 'npcEvent')
      .filter((e) => e.settlementId === this.settlementId && e.eventId > evSince)
      .sort((a, b) => (a.eventId < b.eventId ? -1 : 1));
    for (const e of freshEv) {
      this.lastEvent = e.eventId;
      if (this.primed) out.push({ atMs: nowMs, src: 'event', kind: e.kind, actors: e.versonaId ? [e.versonaId] : [], line: `[${e.kind}] ${e.detail}` });
    }
    const chSince = this.lastChron;
    const freshCh = tbl<ChronicleRow>(conn, 'chronicle')
      .filter((c) => c.settlementId === this.settlementId && c.chronId > chSince)
      .sort((a, b) => (a.chronId < b.chronId ? -1 : 1));
    for (const c of freshCh) {
      this.lastChron = c.chronId;
      if (this.primed) out.push({ atMs: nowMs, src: 'chronicle', kind: c.kind, actors: [c.actorId, c.otherId].filter((x) => x !== 0n), line: `[chronicle:${c.kind}] ${c.detail}` });
    }
    const chatSince = this.lastChat;
    const freshChat = tbl<ChatRow>(conn, 'versonaChat')
      .filter((c) => c.chatId > chatSince)
      .sort((a, b) => (a.chatId < b.chatId ? -1 : 1));
    for (const c of freshChat) {
      this.lastChat = c.chatId;
      if (!agentSet.has(c.versonaId)) continue;
      if (this.primed) out.push({ atMs: nowMs, src: 'chat', kind: c.speaker, actors: [c.versonaId], line: `[chat] ${c.speaker === 'player' ? c.speakerName : labelOf(conn, c.versonaId)}: ${c.text.slice(0, 80)}` });
    }
    const alive = new Set<bigint>();
    for (const e of tbl<EncounterRow>(conn, 'versonaEncounter')) {
      if (e.settlementId !== this.settlementId) continue;
      alive.add(e.encounterId);
      const sig = `${e.state}/${e.tier}`;
      const prev = this.encounters.get(e.encounterId);
      const actors = e.bId !== 0n ? [e.aId, e.bId] : [e.aId];
      const who = e.bId !== 0n ? `${labelOf(conn, e.aId)} ↔ ${labelOf(conn, e.bId)}` : `${labelOf(conn, e.aId)} vs player`;
      if (prev === undefined) {
        if (this.primed) out.push({ atMs: nowMs, src: 'encounter', kind: e.kind, actors, line: `[encounter] ${who} begins (${e.kind}${e.tier ? `, ${e.tier}` : ''})` });
      } else if (prev !== sig && this.primed) {
        out.push({ atMs: nowMs, src: 'encounter', kind: e.state, actors, line: `[encounter] ${who} → ${e.state}${e.tier ? ` (${e.tier})` : ''}` });
      }
      this.encounters.set(e.encounterId, sig);
    }
    for (const idd of [...this.encounters.keys()]) {
      if (!alive.has(idd)) {
        this.encounters.delete(idd);
        if (this.primed) out.push({ atMs: nowMs, src: 'encounter', kind: 'ended', actors: [], line: `[encounter] concluded (#${idd})` });
      }
    }
    // Favor-ledger diffs: loans and repayments between towners ARE interactions
    // (the loan economy dominates versona emotional life) — surface them.
    for (const f of tbl<FavorRow>(conn, 'versonaFavor')) {
      if (!agentSet.has(f.aId) && !agentSet.has(f.bId)) continue;
      const prev = this.favors.get(f.favorId);
      if (prev !== f.balance) {
        this.favors.set(f.favorId, f.balance);
        if (this.primed && prev !== undefined) {
          const delta = f.balance - prev;
          out.push({
            atMs: nowMs, src: 'event', kind: delta !== 0 && Math.abs(f.balance) < Math.abs(prev) ? 'repayment' : 'loan',
            actors: [f.aId, f.bId],
            line: `[favor] ${labelOf(conn, f.aId)} ↔ ${labelOf(conn, f.bId)}: balance ${prev} → ${f.balance}ⓒ (${f.note.slice(0, 40)})`,
          });
        } else if (this.primed && prev === undefined) {
          out.push({ atMs: nowMs, src: 'event', kind: 'loan', actors: [f.aId, f.bId], line: `[favor] new debt: ${labelOf(conn, f.aId)} ↔ ${labelOf(conn, f.bId)} ${f.balance}ⓒ (${f.note.slice(0, 40)})` });
        }
      }
    }
    this.primed = true;
    return out;
  }
}

export function interactionMatrix(interactions: LabInteraction[]): Map<string, { pair: [bigint, bigint]; count: number; kinds: Map<string, number> }> {
  const m = new Map<string, { pair: [bigint, bigint]; count: number; kinds: Map<string, number> }>();
  for (const i of interactions) {
    if (i.actors.length < 2) continue;
    for (let a = 0; a < i.actors.length; a++) {
      for (let b = a + 1; b < i.actors.length; b++) {
        const k = pairKey(i.actors[a], i.actors[b]);
        let e = m.get(k);
        if (!e) { e = { pair: i.actors[a] < i.actors[b] ? [i.actors[a], i.actors[b]] : [i.actors[b], i.actors[a]], count: 0, kinds: new Map() }; m.set(k, e); }
        e.count++;
        e.kinds.set(i.kind, (e.kinds.get(i.kind) ?? 0) + 1);
      }
    }
  }
  return m;
}

// ─── Cast selection ──────────────────────────────────────────────────────────

export interface LabCast { worldId: bigint; settlementId: bigint; center: { x: number; z: number }; ids: bigint[] }

const CHILD_AGE_MICROS = 48n * 3600n * 1_000_000n;

export function adultsOf(conn: unknown, settlementId: bigint, nowMicros: bigint): AgentRow[] {
  const lineage = new Map(tbl<LineageRow>(conn, 'versonaLineage').map((l) => [l.childId, l]));
  const busy = new Set<bigint>();
  for (const e of tbl<EncounterRow>(conn, 'versonaEncounter')) { busy.add(e.aId); if (e.bId !== 0n) busy.add(e.bId); }
  return tbl<AgentRow>(conn, 'npcAgent').filter((a) => {
    if (a.settlementId !== settlementId || busy.has(a.versonaId)) return false;
    const l = lineage.get(a.versonaId);
    return !(l && nowMicros - l.bornAt.microsSinceUnixEpoch < CHILD_AGE_MICROS);
  });
}

/** Pick the cast: `settlementId` given (probe: nearest), or the densest one.
 *  Prefers job diversity so the report reads like a town, not a guild. */
export function pickCast(conn: unknown, nowMs: number, settlementId?: bigint, size = 6): LabCast | null {
  const agents = tbl<AgentRow>(conn, 'npcAgent');
  if (agents.length === 0) return null;
  let sid = settlementId;
  if (sid === undefined) {
    const counts = new Map<bigint, number>();
    for (const a of agents) counts.set(a.settlementId, (counts.get(a.settlementId) ?? 0) + 1);
    sid = [...counts.entries()].sort((x, y) => y[1] - x[1])[0][0];
  }
  const nowMicros = BigInt(Math.round(nowMs * 1000));
  const pool = adultsOf(conn, sid, nowMicros);
  if (pool.length < 2) return null;
  const picked: AgentRow[] = [];
  const seenJobs = new Set<string>();
  for (const a of pool) { if (!seenJobs.has(a.job) && picked.length < size) { picked.push(a); seenJobs.add(a.job); } }
  for (const a of pool) { if (picked.length >= size) break; if (!picked.includes(a)) picked.push(a); }
  const econ = tbl<EconRow>(conn, 'settlementEcon').find((e) => e.settlementId === sid);
  return {
    worldId: picked[0].worldId, settlementId: sid,
    center: econ ? { x: econ.centerX, z: econ.centerZ } : { x: picked[0].homeX, z: picked[0].homeZ },
    ids: picked.map((a) => a.versonaId),
  };
}

// ─── Injection ledger (everything staged gets restored) ──────────────────────

class Injector {
  private statPrior = new Map<string, bigint>();          // `${id}:${field}` → prior
  private pairPrior = new Map<string, { a: bigint; b: bigint; fam: number }>();
  private grudgePrior = new Map<string, { v: bigint; t: bigint; intensity: number; cause: string }>();
  readonly affectTouched = new Set<bigint>();

  constructor(private readonly conn: unknown, private readonly r: LabReducers) {}

  async stat(id: bigint, field: string, value: bigint): Promise<void> {
    const k = `${id}:${field}`;
    if (!this.statPrior.has(k)) {
      // joy/anger/fear/hungerStreak live on npc_mind; everything else on npc_agent.
      const src = ['joy', 'anger', 'fear', 'hungerStreak'].includes(field)
        ? tbl<MindRow>(this.conn, 'npcMind').find((x) => x.versonaId === id)
        : tbl<AgentRow>(this.conn, 'npcAgent').find((x) => x.versonaId === id);
      const cur = (src as unknown as Record<string, unknown> | undefined)?.[field];
      this.statPrior.set(k, typeof cur === 'bigint' ? cur : BigInt(Math.round(Number(cur ?? 0))));
    }
    await this.r.debugSetAgentStat({ versonaId: id, field, value });
  }

  async elicit(id: bigint, etype: string, intensity: number, targetId: bigint, causeText: string): Promise<void> {
    this.affectTouched.add(id);
    await this.r.debugElicitEmotion({ versonaId: id, etype, intensity, targetId, causeText });
  }

  async grudge(id: bigint, targetId: bigint, intensity: number, causeText: string): Promise<void> {
    const k = `${id}:${targetId}`;
    if (!this.grudgePrior.has(k)) {
      const g = tbl<GrudgeRow>(this.conn, 'versonaGrudge').find((x) => x.versonaId === id && x.targetId === targetId);
      this.grudgePrior.set(k, { v: id, t: targetId, intensity: g?.intensity ?? 0, cause: g?.causeText ?? '' });
    }
    this.affectTouched.add(id);
    await this.r.debugSetGrudge({ versonaId: id, targetId, intensity, causeText });
  }

  async pair(aId: bigint, bId: bigint, familiarity: number): Promise<void> {
    const k = pairKey(aId, bId);
    if (!this.pairPrior.has(k)) {
      const p = tbl<PairRow>(this.conn, 'versonaPair').find((x) => (x.aId === aId && x.bId === bId) || (x.aId === bId && x.bId === aId));
      this.pairPrior.set(k, { a: aId, b: bId, fam: p?.familiarity ?? 0 });
    }
    await this.r.debugSetPair({ aId, bId, familiarity });
  }

  async goal(id: bigint, x: number, z: number, state: string, ttlSeconds: number): Promise<void> {
    await this.r.setVersonaGoal({ versonaId: id, x, z, state, ttlSeconds }); // TTL-expiring — nothing to restore
  }

  /** Stats back, grudges/pairs back to prior, clear-affect on treated agents. */
  async restoreAll(log: (s: string) => void): Promise<void> {
    for (const [k, v] of this.statPrior) {
      const [id, field] = k.split(':');
      await this.r.debugSetAgentStat({ versonaId: BigInt(id), field, value: v }).catch((e: Error) => log(`restore ${k}: ${e.message}`));
    }
    for (const g of this.grudgePrior.values()) {
      await this.r.debugSetGrudge({ versonaId: g.v, targetId: g.t, intensity: g.intensity, causeText: g.cause }).catch((e: Error) => log(`restore grudge: ${e.message}`));
    }
    for (const p of this.pairPrior.values()) {
      await this.r.debugSetPair({ aId: p.a, bId: p.b, familiarity: p.fam }).catch((e: Error) => log(`restore pair: ${e.message}`));
    }
    for (const id of this.affectTouched) {
      await this.r.debugClearAffect({ versonaId: id }).catch((e: Error) => log(`clear affect #${id}: ${e.message}`));
    }
    log(`restored: ${this.statPrior.size} stats, ${this.grudgePrior.size} grudges, ${this.pairPrior.size} pairs, affect cleared on ${this.affectTouched.size}`);
  }
}

// ─── Scenario engine ─────────────────────────────────────────────────────────

export interface LabCheck { label: string; ok: boolean; hard: boolean; detail?: string }

export interface ScenarioResult {
  name: string; title: string;
  startedMs: number; durationMs: number; ran: boolean; skippedWhy?: string;
  checks: LabCheck[]; observations: string[];
  interactions: LabInteraction[];
  timeline: Map<bigint, VersonaSnapshot[]>;
}

export interface LabRunOptions {
  windowMs?: number;      // base observe window per scenario (default 90_000)
  sampleMs?: number;      // sampling cadence (default 3_000)
  canInject: boolean;     // admin available?
  scenarios?: string[];   // subset (default all)
  log: (line: string) => void;
  sleep: (ms: number) => Promise<void>;
  onInteraction?: (i: LabInteraction) => void;
  onScenario?: (r: ScenarioResult) => void;
}

interface ScenarioEnv {
  conn: unknown; cast: LabCast; inj: Injector | null;
  windowMs: number; sampleMs: number;
  result: ScenarioResult;
  stagedAtMicros: bigint;
  log: (line: string) => void;
  check(label: string, ok: boolean, hard: boolean, detail?: string): void;
  observe(line: string): void;
}

interface ScenarioDef {
  name: string; title: string; inject: boolean;
  /** cast slot indices this scenario touches */
  slots: number[];
  stage?(env: ScenarioEnv, ids: bigint[]): Promise<void>;
  /** called every sample tick — re-assert goals etc. */
  tick?(env: ScenarioEnv, ids: bigint[], elapsedMs: number): Promise<void>;
  verify(env: ScenarioEnv, ids: bigint[]): void;
}

const dist2 = (ax: number, az: number, bx: number, bz: number): number => Math.hypot(ax - bx, az - bz);

function snapshotsOf(env: ScenarioEnv, id: bigint): VersonaSnapshot[] { return env.result.timeline.get(id) ?? []; }

function minPairDist(env: ScenarioEnv, a: bigint, b: bigint): number {
  const sa = snapshotsOf(env, a), sb = snapshotsOf(env, b);
  let best = Infinity;
  for (let i = 0; i < Math.min(sa.length, sb.length); i++) best = Math.min(best, dist2(sa[i].x, sa[i].z, sb[i].x, sb[i].z));
  return best;
}

const SCENARIOS: ScenarioDef[] = [
  {
    name: 'baseline', title: 'Baseline — organic town life, no interference', inject: false, slots: [],
    verify(env) {
      const covered = env.cast.ids.filter((id) => snapshotsOf(env, id).length >= 3);
      env.check(`sampled all ${env.cast.ids.length} cast members (≥3 snapshots each)`, covered.length === env.cast.ids.length, true, `${covered.length}/${env.cast.ids.length}`);
      const moved = env.cast.ids.some((id) => {
        const s = snapshotsOf(env, id);
        return s.length >= 2 && dist2(s[0].x, s[0].z, s[s.length - 1].x, s[s.length - 1].z) > 0.5;
      });
      env.check('the town is alive (movement or interactions observed)', moved || env.result.interactions.length > 0, true);
      const acts = new Map<string, number>();
      for (const id of env.cast.ids) for (const s of snapshotsOf(env, id)) acts.set(s.activity, (acts.get(s.activity) ?? 0) + 1);
      env.observe(`activity mix: ${[...acts.entries()].sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k}×${v}`).join(' · ')}`);
      env.observe(`${env.result.interactions.length} interactions observed organically`);
    },
  },
  {
    name: 'hunger', title: 'Hunger crisis — sustained famine on one versona', inject: true, slots: [0],
    async stage(env, [a]) {
      await env.inj!.stat(a, 'hunger', 96n);
      await env.inj!.stat(a, 'wallet', 0n);
      await env.inj!.stat(a, 'hungerStreak', 4n);
      env.log(`  starving ${labelOf(env.conn, a)} (hunger 96, broke, streak 4) — crisis fires at streak 5`);
    },
    async tick(env, [a], elapsedMs) {
      // The pantry metabolism legitimately feeds them back down — sustained
      // famine means re-asserting hunger. STOP once the crisis answered
      // (re-zeroing the wallet after a loan just farms more loans and stains
      // the favor ledger).
      if (emotionsSince(env.conn, a, env.stagedAtMicros).length > 0) return;
      if (elapsedMs < env.windowMs * 0.67) {
        await env.inj!.stat(a, 'hunger', 96n).catch(() => undefined);
        await env.inj!.stat(a, 'wallet', 0n).catch(() => undefined);
      }
    },
    verify(env, [a]) {
      const peak = Math.max(0, ...snapshotsOf(env, a).map((s) => s.hunger));
      env.check('famine injection landed (hunger ≥ 85 observed)', peak >= 85, true, `peak ${peak}`);
      const felt = emotionsSince(env.conn, a, env.stagedAtMicros);
      const crisis = felt.filter((e) => ['gratitude', 'anger', 'fear', 'distress', 'joy'].includes(e.etype));
      const streakSeen = Math.max(0, ...snapshotsOf(env, a).map((s) => s.hungerStreak));
      const favors = tbl<FavorRow>(env.conn, 'versonaFavor').filter((f) => f.aId === a || f.bId === a);
      const responded = crisis.length > 0 || favors.length > 0;
      env.check('crisis response observed (loan / welfare / starving emotion)', responded, true,
        crisis.map((e) => `${e.etype}←"${e.causeText.slice(0, 40)}"`).join(', ') || `favors:${favors.length}`);
      env.check('hunger streak accumulated toward crisis', streakSeen >= 4, false, `max streak ${streakSeen}`);
      for (const e of crisis.slice(0, 4)) env.observe(`felt ${e.etype}${e.targetId !== 0n ? ` at ${labelOf(env.conn, e.targetId)}` : ''} — "${e.causeText.slice(0, 70)}"`);
      for (const f of favors) env.observe(`favor ledger: #${f.aId}↔#${f.bId} balance ${f.balance}ⓒ (${f.note.slice(0, 40)})`);
    },
  },
  {
    name: 'feud', title: 'Feud — mutual grudges between two versonas', inject: true, slots: [1, 2],
    async stage(env, [b, c]) {
      await env.inj!.grudge(b, c, 90, 'a bitter lab-staged quarrel');
      await env.inj!.grudge(c, b, 90, 'a bitter lab-staged quarrel');
      await env.inj!.elicit(b, 'anger', 75, c, 'that argument still burns');
      await env.inj!.elicit(c, 'resentment', 75, b, 'they started it');
      env.log(`  feud staged: ${labelOf(env.conn, b)} ⇄ ${labelOf(env.conn, c)} (grudge 90 both ways)`);
    },
    verify(env, [b, c]) {
      const g = tbl<GrudgeRow>(env.conn, 'versonaGrudge');
      const bc = g.find((x) => x.versonaId === b && x.targetId === c);
      const cb = g.find((x) => x.versonaId === c && x.targetId === b);
      env.check('mutual grudges hold', !!bc && !!cb, true, `b→c ${bc?.intensity ?? '—'}, c→b ${cb?.intensity ?? '—'}`);
      const angry = emotionsSince(env.conn, b, env.stagedAtMicros).some((e) => e.targetId === c)
        || emotionsSince(env.conn, c, env.stagedAtMicros).some((e) => e.targetId === b);
      env.check('hostile emotions target the rival', angry, true);
      const between = env.result.interactions.filter((i) => i.actors.includes(b) && i.actors.includes(c));
      const cold = between.filter((i) => i.line.includes('cold'));
      const spiral = env.result.interactions.filter((i) => ['feud', 'brawl'].includes(i.kind));
      env.check('hostility expressed (cold encounter / avoidance / feud)', true, false,
        between.length === 0 ? 'they avoided each other (hostile pairs dodge 70% of encounters)' : `${between.length} contacts, ${cold.length} cold`);
      if (spiral.length) for (const s of spiral.slice(0, 3)) env.observe(s.line);
      env.observe(between.length === 0 ? 'rivals kept their distance for the whole window' : `rival contact: ${between.map((i) => i.line).slice(0, 3).join(' | ')}`);
    },
  },
  {
    name: 'friendship', title: 'Friendship — familiar pair steered to meet', inject: true, slots: [3, 4],
    async stage(env, [d, e]) {
      await env.inj!.pair(d, e, 85);
      const m = { x: env.cast.center.x + 18, z: env.cast.center.z + 14 };
      await env.inj!.goal(d, m.x, m.z, 'working', 90);
      await env.inj!.goal(e, m.x + 2, m.z, 'working', 90);
      env.log(`  befriended ${labelOf(env.conn, d)} + ${labelOf(env.conn, e)} (familiarity 85), steering both to (${m.x.toFixed(0)}, ${m.z.toFixed(0)})`);
    },
    async tick(env, [d, e]) {
      // npcSimTick's brain legitimately rewrites goals — keep re-asserting.
      const m = { x: env.cast.center.x + 18, z: env.cast.center.z + 14 };
      await env.inj!.goal(d, m.x, m.z, 'working', 90).catch(() => undefined);
      await env.inj!.goal(e, m.x + 2, m.z, 'working', 90).catch(() => undefined);
    },
    verify(env, [d, e]) {
      const p = tbl<PairRow>(env.conn, 'versonaPair').find((x) => (x.aId === d && x.bId === e) || (x.aId === e && x.bId === d));
      env.check('pair familiarity landed', (p?.familiarity ?? 0) >= 80, true, `familiarity ${p?.familiarity ?? '—'}`);
      const near = minPairDist(env, d, e);
      env.check('the pair converged (within 15 m — encounter range)', near <= 15, false, `min distance ${near === Infinity ? '?' : near.toFixed(1)} m`);
      const met = env.result.interactions.filter((i) => i.actors.includes(d) && i.actors.includes(e));
      env.check('social encounter observed between them', met.length > 0, false,
        met.length ? met[0].line : 'encounter spawns are hash-gated (p≈0.04/tick) — proximity is the staged part');
      for (const i of met.slice(0, 4)) env.observe(i.line);
    },
  },
  {
    name: 'cheer', title: 'Cheer — injected joy lifts a versona', inject: true, slots: [5],
    async stage(env, [f]) {
      const before = tbl<MoodRow>(env.conn, 'npcMood').find((x) => x.versonaId === f);
      env.observe(`PAD before: ${before ? `P ${(before.pleasureX10 / 10).toFixed(1)}` : 'no mood row yet (lazy)'}`);
      await env.inj!.elicit(f, 'joy', 85, 0n, 'a festival held in their honor');
      env.log(`  cheered ${labelOf(env.conn, f)} (joy 85)`);
    },
    verify(env, [f]) {
      const ep = emotionsSince(env.conn, f, env.stagedAtMicros).find((e) => e.etype === 'joy' && e.causeText.includes('festival'));
      env.check('joy episode present with our cause', !!ep, true);
      const mood = tbl<MoodRow>(env.conn, 'npcMood').find((x) => x.versonaId === f);
      env.check('PAD mood row folded (pleasure recorded)', !!mood, true, mood ? `P ${(mood.pleasureX10 / 10).toFixed(1)} A ${(mood.arousalX10 / 10).toFixed(1)} D ${(mood.dominanceX10 / 10).toFixed(1)}` : undefined);
      const top = snapshotsOf(env, f).at(-1)?.emotions[0];
      if (top) env.observe(`dominant feeling at window end: ${top.etype} ${top.eff} — "${top.cause.slice(0, 50)}"`);
    },
  },
  {
    name: 'command', title: 'Command — direct behavior control (walk there)', inject: true, slots: [5],
    async stage(env, [f]) {
      const s0 = sampleVersona(env.conn, f, Date.now());
      const t = { x: env.cast.center.x - 12, z: env.cast.center.z - 16 };
      env.result.observations.push(`__cmd:${t.x},${t.z},${s0 ? dist2(s0.x, s0.z, t.x, t.z).toFixed(1) : '?'}`);
      await env.inj!.goal(f, t.x, t.z, 'patrolling', 60);
      env.log(`  commanding ${labelOf(env.conn, f)} to (${t.x.toFixed(0)}, ${t.z.toFixed(0)})${s0 ? ` — ${dist2(s0.x, s0.z, t.x, t.z).toFixed(0)} m away` : ''}`);
    },
    async tick(env, [f]) {
      const t = { x: env.cast.center.x - 12, z: env.cast.center.z - 16 };
      await env.inj!.goal(f, t.x, t.z, 'patrolling', 60).catch(() => undefined);
    },
    verify(env, [f]) {
      const marker = env.result.observations.find((o) => o.startsWith('__cmd:'));
      env.result.observations = env.result.observations.filter((o) => !o.startsWith('__cmd:'));
      if (!marker) { env.check('command staged', false, true); return; }
      const [tx, tz, d0s] = marker.slice(6).split(',');
      const t = { x: Number(tx), z: Number(tz) };
      const d0 = Number(d0s) || Infinity;
      let best = Infinity;
      for (const s of snapshotsOf(env, f)) best = Math.min(best, dist2(s.x, s.z, t.x, t.z));
      env.observe(`start ${d0 === Infinity ? '?' : d0.toFixed(0)} m from target, closest approach ${best === Infinity ? '?' : best.toFixed(1)} m`);
      // Versonas walk ~0.4 m/s — a short window can't close half of a far
      // start, so the obedience check is hard only with a real window.
      const hardEnough = env.windowMs >= 60_000;
      env.check('the versona obeyed (closed ≥50% of the distance or arrived)', best <= Math.max(8, d0 * 0.5), hardEnough, `${d0.toFixed(0)} m → ${best.toFixed(1)} m${hardEnough ? '' : ' (short window — soft)'}`);
      env.check('arrived at the commanded spot (≤8 m)', best <= 8, false, `${best.toFixed(1)} m`);
    },
  },
];

export const LAB_SCENARIO_NAMES = SCENARIOS.map((s) => s.name);

export interface LabRun {
  startedAtMs: number; worldId: bigint; settlementId: bigint;
  cast: { id: bigint; label: string }[];
  scenarios: ScenarioResult[];
  interactions: LabInteraction[];
  dossiers: Map<bigint, string[]>;
  checks: { passed: number; failed: number; hardFailed: number };
}

/** Run the whole lab: scenarios in order, one shared interaction watch, full
 *  restoration at the end. The engine never throws for behavior — only for a
 *  missing cast/connection. */
export async function runLab(conn: unknown, cast: LabCast, opts: LabRunOptions): Promise<LabRun> {
  const windowMs = opts.windowMs ?? 90_000;
  const sampleMs = opts.sampleMs ?? 3_000;
  const r = labReducers(conn);
  const inj = opts.canInject && r ? new Injector(conn, r) : null;
  const watch = new InteractionWatch(cast.settlementId);
  watch.drain(conn, Date.now()); // prime — pre-existing rows are history, not observations
  const wanted = opts.scenarios ?? LAB_SCENARIO_NAMES;
  const run: LabRun = {
    startedAtMs: Date.now(), worldId: cast.worldId, settlementId: cast.settlementId,
    cast: cast.ids.map((id) => ({ id, label: labelOf(conn, id) })),
    scenarios: [], interactions: [], dossiers: new Map(),
    checks: { passed: 0, failed: 0, hardFailed: 0 },
  };
  opts.log(`cast of ${cast.ids.length} in settlement ${cast.settlementId}: ${run.cast.map((c) => c.label).join(', ')}`);

  for (const def of SCENARIOS) {
    if (!wanted.includes(def.name)) continue;
    const result: ScenarioResult = {
      name: def.name, title: def.title, startedMs: Date.now(), durationMs: 0,
      ran: false, checks: [], observations: [], interactions: [], timeline: new Map(),
    };
    run.scenarios.push(result);
    if (def.inject && !inj) {
      result.skippedWhy = 'no admin capability — observe-only mode';
      opts.log(`⤳ ${def.name}: skipped (${result.skippedWhy})`);
      opts.onScenario?.(result);
      continue;
    }
    const ids = def.slots.map((i) => cast.ids[i % cast.ids.length]);
    const env: ScenarioEnv = {
      conn, cast, inj, windowMs, sampleMs, result,
      // 10 s grace against client↔server clock skew (server stamps episodes).
      stagedAtMicros: BigInt(Date.now() - 10_000) * 1000n,
      log: opts.log,
      check: (label, ok, hard, detail) => {
        result.checks.push({ label, ok, hard, detail });
        if (ok) run.checks.passed++; else { run.checks.failed++; if (hard) run.checks.hardFailed++; }
        opts.log(`  ${ok ? '✓' : hard ? '✗' : '△'} ${label}${detail ? ` — ${detail}` : ''}`);
      },
      observe: (line) => { result.observations.push(line); opts.log(`  · ${line}`); },
    };
    opts.log(`▶ ${def.title}`);
    result.ran = true;
    try {
      await def.stage?.(env, ids);
    } catch (e) {
      env.check(`${def.name} staged`, false, true, (e as Error).message);
      opts.onScenario?.(result);
      continue;
    }
    const t0 = Date.now();
    let lastTick = 0;
    while (Date.now() - t0 < windowMs) {
      await opts.sleep(sampleMs);
      const now = Date.now();
      for (const id of cast.ids) {
        const s = sampleVersona(conn, id, now);
        if (s) {
          const arr = result.timeline.get(id) ?? [];
          arr.push(s);
          result.timeline.set(id, arr);
        }
      }
      const fresh = watch.drain(conn, now);
      for (const i of fresh) { result.interactions.push(i); run.interactions.push(i); opts.onInteraction?.(i); }
      if (def.tick && now - lastTick >= 10_000) { lastTick = now; await def.tick(env, ids, now - t0).catch(() => undefined); }
    }
    def.verify(env, ids);
    result.durationMs = Date.now() - result.startedMs;
    opts.onScenario?.(result);
  }

  if (inj) {
    opts.log('▶ restore — returning the cast to their lives');
    await inj.restoreAll(opts.log);
  }
  for (const id of cast.ids) run.dossiers.set(id, dossierOf(conn, id));
  return run;
}

// ─── Dossier + report rendering ──────────────────────────────────────────────

export function dossierOf(conn: unknown, id: bigint): string[] {
  const out: string[] = [];
  const a = tbl<AgentRow>(conn, 'npcAgent').find((x) => x.versonaId === id);
  if (!a) return ['(no agent row)'];
  const v = tbl<VersonaRow>(conn, 'versona').find((x) => x.versonaId === id);
  out.push(`${v?.archetype ?? '?'} · ${a.job} · bold ${a.boldness} greed ${a.greed} dilig ${a.diligence} soc ${a.sociability}`);
  out.push(`needs: hunger ${a.hunger} rest ${a.needRest} social ${a.needSocial} mood ${a.mood} · wallet ${a.wallet}ⓒ${a.carry ? ` · carrying ${a.carry} ${a.carryGood}` : ''}`);
  const lin = tbl<LineageRow>(conn, 'versonaLineage');
  const mine = lin.find((l) => l.childId === id);
  const kids = lin.filter((l) => l.parentA === id || l.parentB === id).length;
  if (mine || kids) out.push(`family: gen ${mine?.generation ?? 0}${mine ? `, child of #${mine.parentA} + #${mine.parentB}` : ''}${kids ? `, ${kids} child${kids > 1 ? 'ren' : ''}` : ''}`);
  const faith = tbl<FaithRow>(conn, 'faithAdherence').find((f) => f.versonaId === id);
  if (faith) out.push(`faith: ${faith.faithId} (${faith.intensity})`);
  const pairs = tbl<PairRow>(conn, 'versonaPair').filter((p) => p.aId === id || p.bId === id).sort((x, y) => y.familiarity - x.familiarity).slice(0, 3);
  if (pairs.length) out.push(`closest: ${pairs.map((p) => `${labelOf(conn, p.aId === id ? p.bId : p.aId)} (${p.familiarity})`).join(', ')}`);
  const grudges = tbl<GrudgeRow>(conn, 'versonaGrudge').filter((gr) => gr.versonaId === id);
  if (grudges.length) out.push(`grudges: ${grudges.map((gr) => `vs ${labelOf(conn, gr.targetId)} (${gr.intensity})`).join(', ')}`);
  const mems = tbl<MemoryRow>(conn, 'versonaMemory').filter((m) => m.versonaId === id).sort((x, y) => y.importance - x.importance).slice(0, 3);
  // PROVENANCE (2026-09-15): who told them, how many hands it passed through, how much they trust it.
  interface ProvRow { memoryId: bigint; tellerId: bigint; originId: bigint; hops: number; confidence: number }
  const provs = new Map<bigint, ProvRow>();
  for (const p of tbl<ProvRow>(conn, 'memoryProvenance')) provs.set(p.memoryId, p);
  for (const m of mems) {
    const p = provs.get(m.memoryId);
    const via = p && m.kind === 'rumor'
      ? (p.tellerId > 0n ? ` — told by ${labelOf(conn, p.tellerId)}${p.originId > 0n && p.originId !== p.tellerId ? `, who had it from ${labelOf(conn, p.originId)}` : ''} (${p.hops} hop${p.hops === 1 ? '' : 's'}, trusts it ${p.confidence} %)` : ` — someone said (trusts it ${p.confidence} %)`)
      : '';
    out.push(`memory [${m.importance}]${m.kind === 'rumor' ? ' (heard)' : ''}: ${m.text.slice(0, 90)}${via}`);
  }
  return out;
}

export function renderLabReportMd(run: LabRun): string {
  const L: string[] = [];
  L.push(`# Versona Lab — group simulation report`);
  L.push('');
  L.push(`- **When:** ${new Date(run.startedAtMs).toISOString()}`);
  L.push(`- **Settlement:** ${run.settlementId} (world ${run.worldId})`);
  L.push(`- **Cast:** ${run.cast.map((c) => c.label).join(', ')}`);
  L.push(`- **Checks:** ${run.checks.passed} passed · ${run.checks.failed} soft/hard flags (${run.checks.hardFailed} hard)`);
  L.push('');
  for (const s of run.scenarios) {
    L.push(`## ${s.title}`);
    if (!s.ran) { L.push(`*Skipped: ${s.skippedWhy}*`); L.push(''); continue; }
    for (const c of s.checks) L.push(`- ${c.ok ? '✅' : c.hard ? '❌' : '⚠️'} ${c.label}${c.detail ? ` — ${c.detail}` : ''}`);
    for (const o of s.observations) L.push(`- 👁 ${o}`);
    if (s.interactions.length) {
      L.push('');
      L.push(`<details><summary>${s.interactions.length} interactions during this window</summary>`);
      L.push('');
      for (const i of s.interactions.slice(0, 40)) L.push(`- \`${new Date(i.atMs).toISOString().slice(11, 19)}\` ${i.line}`);
      if (s.interactions.length > 40) L.push(`- … ${s.interactions.length - 40} more`);
      L.push('');
      L.push('</details>');
    }
    L.push('');
  }
  const matrix = interactionMatrix(run.interactions);
  if (matrix.size) {
    L.push('## Who interacted with whom');
    L.push('');
    L.push('| pair | contacts | kinds |');
    L.push('|---|---|---|');
    for (const e of [...matrix.values()].sort((x, y) => y.count - x.count).slice(0, 20)) {
      L.push(`| #${e.pair[0]} ↔ #${e.pair[1]} | ${e.count} | ${[...e.kinds.entries()].map(([k, v]) => `${k}×${v}`).join(', ')} |`);
    }
    L.push('');
  }
  L.push('## Cast dossiers (post-run)');
  L.push('');
  for (const c of run.cast) {
    L.push(`### ${c.label}`);
    for (const line of run.dossiers.get(c.id) ?? []) L.push(`- ${line}`);
    L.push('');
  }
  return L.join('\n');
}

/** JSON-safe serialization (bigints → strings, Maps → objects). */
export function labRunToJson(run: LabRun): string {
  return JSON.stringify(run, (_k, v: unknown) => {
    if (typeof v === 'bigint') return v.toString();
    if (v instanceof Map) return Object.fromEntries([...v.entries()].map(([k, val]) => [String(k), val]));
    return v;
  });
}
